import { SessionRun } from "../models/audit/sessionRun";
import {
  AuditSession,
  ProcedureColumn,
  SourceRow,
} from "../models/audit/auditSession";
import {
  CheckResultSummary,
  DrySessionSourceRow,
} from "../models/audit/procedureSessionTableShell";
import { WorkpaperDataSlim } from "../models/audit/workpaperData";
import { isVerdictColumn } from "../models/audit/attributeTestTranslator";

type WorkpaperDataType = WorkpaperDataSlim | null;

/**
 * The lock-bearing projections a verdict column's cell is read from.
 *
 * Verdict locks live on the dry source-row envelope, not on the heavy row:
 * `AttributeTestColumnResult` carries no `lockedBy` field at all, so a caller
 * holding only a hydrated `SourceRow` cannot answer the question. Analytics is
 * resolved from the same projection even though its heavy result *does* carry
 * `lockedBy`, so there is one path to the fact and the lock check cannot
 * disagree with the badge the user is looking at.
 */
type VerdictLockSummaries = Partial<
  Pick<
    DrySessionSourceRow,
    "attributeTestResultsSummary" | "analyticsResultsSummary"
  >
>;

// Utility function to get the latest check result. Locked-first, then newest
// (MS-2533): a locked result is the user's explicit "this stands" and always
// wins; otherwise a rerun's fresh result shows. Protection for manual reviews
// is the explicit per-cell lock, not an implicit human-first rule.
export function getLatestCheckResult(
  sourceRow: AuditSession["sourceRows"][number],
  evalColumnId: string,
) {
  const locked = sourceRow.checkResults
    .filter((r) => r.evalColumnId === evalColumnId && r.lockedBy != null)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )?.[0];

  const latest = sourceRow.checkResults
    .filter((r) => r.evalColumnId === evalColumnId)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )?.[0];

  return locked || latest;
}

/**
 * Parallel of `getLatestCheckResult` that operates on the slim
 * `checkResultsSummary[]` projection shipped on `DrySessionSourceRow`. The
 * workpaper table's `EvalColumnCell` uses this to render its badge without
 * firing the heavy `useSourceRow` per visible row.
 *
 * Selection semantics match the heavy variant: prefer the most recent
 * locked entry (MS-2533 locked-first); otherwise the most recent entry of
 * any reviewer type.
 */
export function getLatestCheckResultSummary(
  summary: CheckResultSummary[] | undefined,
  evalColumnId: string,
): CheckResultSummary | undefined {
  if (!summary) return undefined;

  const sortByCreatedAtDesc = (a: CheckResultSummary, b: CheckResultSummary) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

  const locked = summary
    .filter((r) => r.evalColumnId === evalColumnId && r.lockedBy != null)
    .sort(sortByCreatedAtDesc)[0];

  const latest = summary
    .filter((r) => r.evalColumnId === evalColumnId)
    .sort(sortByCreatedAtDesc)[0];

  return locked || latest;
}

/**
 * Returns the most recent `AttributeTestResultSummary` for the given column,
 * preferring locked entries (MS-2533 locked-first). Parallel to
 * `getLatestCheckResultSummary` and consumed by `AttrTestColumnCell` in
 * `WorkPaperTableV2`.
 */
export function getLatestAttributeTestResultSummary(
  summary: DrySessionSourceRow["attributeTestResultsSummary"] | undefined,
  attributeTestColumnId: string,
): DrySessionSourceRow["attributeTestResultsSummary"][number] | undefined {
  if (!summary) return undefined;

  type SummaryEntry =
    DrySessionSourceRow["attributeTestResultsSummary"][number];
  const sortByCreatedAtDesc = (a: SummaryEntry, b: SummaryEntry) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

  const locked = summary
    .filter(
      (r) =>
        r.attributeTestColumnId === attributeTestColumnId && r.lockedBy != null,
    )
    .sort(sortByCreatedAtDesc)[0];

  const latest = summary
    .filter((r) => r.attributeTestColumnId === attributeTestColumnId)
    .sort(sortByCreatedAtDesc)[0];

  return locked || latest;
}

/**
 * The live `AnalyticsResultSummary` for the given column, if the cell has one.
 *
 * An analytics verdict is not ranked; the live row is the one not superseded.
 * A ranking returns the wrong row the moment a born-superseded re-judgment sits
 * beside the human override it was written already-superseded to defer to —
 * which is the arrangement supersession exists to create. It is deliberately
 * unlike `getLatestAttributeTestResultSummary`, whose table has no supersession
 * and whose lock is what decides the cell.
 *
 * The rule cannot be re-applied here: the summary wire shape carries no
 * `supersededAt`. It does not need to be. The projection behind this payload
 * selects live rows only and `uq_analytics_result_current` admits at most one
 * per cell, so a column's entry is either present or absent — never a set to
 * choose from. This reads that entry.
 *
 * A second entry means that guarantee broke upstream. Choosing between them
 * would be a ranking by array order, so the cell reports no resolvable verdict
 * and says so in the console; the server remains the authority on whether the
 * cell is locked. The grid recovers the two-verdict case separately and marks
 * the cell rather than leaving it blank.
 */
export function getLiveAnalyticsResultSummary(
  summary: DrySessionSourceRow["analyticsResultsSummary"] | undefined,
  analyticsColumnId: string,
): DrySessionSourceRow["analyticsResultsSummary"][number] | undefined {
  if (!summary) return undefined;

  const live = summary.filter((r) => r.analyticsColumnId === analyticsColumnId);

  if (live.length > 1) {
    console.error(
      `Analytics column ${analyticsColumnId} arrived with ${live.length} live verdicts; ` +
        "at most one is live per cell. Reporting none rather than choosing between them.",
    );
    return undefined;
  }

  return live[0];
}

// Utility function to check if a support column cell is locked
export function isSupportColumnLocked(
  sourceRowId: string,
  supportColumnId: string,
  workpaperData: WorkpaperDataType,
): boolean {
  const cell = workpaperData?.data[sourceRowId]?.find(
    (d) => d.supportColumnId === supportColumnId,
  );
  return !!cell?.lockedBy;
}

// Utility function to check if an eval column result is locked
export function isEvalColumnLocked(
  sourceRow: AuditSession["sourceRows"][number],
  evalColumnId: string,
): boolean {
  const result = getLatestCheckResult(sourceRow, evalColumnId);
  return !!result?.lockedBy;
}

// Utility function to check if an attribute-test column result is locked.
// Uses the same latest-selection as the grid badge so the lock check always
// agrees with the result the user is looking at.
export function isAttributeTestColumnLocked(
  summary: DrySessionSourceRow["attributeTestResultsSummary"] | undefined,
  attributeTestColumnId: string,
): boolean {
  const result = getLatestAttributeTestResultSummary(
    summary,
    attributeTestColumnId,
  );
  return !!result?.lockedBy;
}

// Uses the same live-verdict selection as the grid badge, so the lock check
// always agrees with the result the user is looking at.
export function isAnalyticsColumnLocked(
  summary: DrySessionSourceRow["analyticsResultsSummary"] | undefined,
  analyticsColumnId: string,
): boolean {
  const result = getLiveAnalyticsResultSummary(summary, analyticsColumnId);
  return !!result?.lockedBy;
}

/**
 * Whether a verdict column's cell is locked, dispatching on which kind of
 * verdict it holds. Returns `false` for a column that reaches no verdict, and
 * for a verdict column whose summaries the caller did not supply. Note this
 * reports *unlocked*, which permits a rerun — the frontend defers to the
 * server, which skips locked cells itself, rather than blocking on a state it
 * cannot resolve.
 */
export function isVerdictColumnLocked(
  column: ProcedureColumn,
  summaries: VerdictLockSummaries | undefined,
): boolean {
  if (column.attributeTestColumn) {
    return isAttributeTestColumnLocked(
      summaries?.attributeTestResultsSummary,
      column.attributeTestColumn.id,
    );
  }
  if (column.analyticsColumn) {
    return isAnalyticsColumnLocked(
      summaries?.analyticsResultsSummary,
      column.analyticsColumn.id,
    );
  }
  return false;
}

// Utility function to check if any column is locked.
//
// `verdictSummaries` is what makes the attribute-test and analytics arms
// answerable at all: their locks are not reachable from the heavy `sourceRow`
// (see `VerdictLockSummaries`). Before it, both kinds fell through to `false`
// and reported every verdict cell as unlocked.
export function isColumnLocked(
  column: AuditSession["procedure"]["columns"][number],
  sourceRow: AuditSession["sourceRows"][number],
  workpaperData: WorkpaperDataType,
  verdictSummaries?: VerdictLockSummaries,
): boolean {
  if (column.supportColumn) {
    return isSupportColumnLocked(
      sourceRow.id,
      column.supportColumn.id,
      workpaperData,
    );
  } else if (column.evaluatedColumn) {
    return isEvalColumnLocked(sourceRow, column.evaluatedColumn.id);
  } else if (isVerdictColumn(column)) {
    return isVerdictColumnLocked(column, verdictSummaries);
  }
  return false;
}

const ANALYTICS_ENGINE = "analytics";

/**
 * Whether a session run is a Generate's analytics row: one sample's analytics
 * columns, resolved by the Generate's analytics workflow beside the worker's
 * row for the same sample.
 */
export function isAnalyticsRun(run: SessionRun): boolean {
  return run.engine === ANALYTICS_ENGINE;
}

/**
 * One Run Log entry: a sample's run as the auditor sees it, with every row
 * behind it. `run` is the row the entry is named and copied by.
 */
export interface RunLogEntry {
  run: SessionRun;
  runs: SessionRun[];
}

const sampleOfGenerate = (run: SessionRun): string =>
  `${run.eventRunId ?? ""}:${run.sourceRowId}`;

const createdAtMs = (run: SessionRun): number => {
  const ms = new Date(run.createdAt).getTime();
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
};

/**
 * Whether `run` is a later attempt than `other`. An unreadable `createdAt`
 * counts as oldest, and a tie falls to the id, so the same row wins on every
 * render whatever order the rows arrive in.
 */
const isLaterAttempt = (run: SessionRun, other: SessionRun): boolean => {
  const runMs = createdAtMs(run);
  const otherMs = createdAtMs(other);
  if (runMs !== otherMs) {
    return runMs > otherMs;
  }
  return run.id > other.id;
};

/**
 * The Run Log's entries for a set of session runs: one per row, except that a
 * Generate's analytics row joins the entry of the worker row it sits beside.
 *
 * A mixed sample carries two rows under one Generate, and listed separately
 * the log would show every such sample twice and count it twice. The
 * analytics row joins the newest worker row of its sample and Generate: a
 * retry writes a fresh worker row beside the failed or superseded first
 * attempt, and the analytics columns belong with the attempt still live, so
 * the earlier attempt keeps its own state. A sample whose only work was
 * analytics has no worker row, and its analytics row stands as the entry.
 * Worker rows are never merged with each other, so a sample retried under one
 * Generate lists each attempt as its own entry.
 */
export function runLogEntries(sessionRuns: SessionRun[]): RunLogEntry[] {
  const newestWorkerRun = new Map<string, SessionRun>();
  for (const run of sessionRuns) {
    if (isAnalyticsRun(run)) {
      continue;
    }
    const key = sampleOfGenerate(run);
    const current = newestWorkerRun.get(key);
    if (!current || isLaterAttempt(run, current)) {
      newestWorkerRun.set(key, run);
    }
  }

  const entries: RunLogEntry[] = [];
  const entryByRunId = new Map<string, RunLogEntry>();
  const joining: SessionRun[] = [];
  for (const run of sessionRuns) {
    if (isAnalyticsRun(run) && newestWorkerRun.has(sampleOfGenerate(run))) {
      joining.push(run);
    } else {
      const entry = { run, runs: [run] };
      entries.push(entry);
      entryByRunId.set(run.id, entry);
    }
  }
  for (const run of joining) {
    const worker = newestWorkerRun.get(sampleOfGenerate(run));
    const entry = worker ? entryByRunId.get(worker.id) : undefined;
    if (entry) {
      entry.runs.push(run);
    }
  }
  return entries;
}

/**
 * The columns a Run Log entry's rows target between them, each once, or
 * `null` where any row is row-wide, since a row-wide row already covers every
 * column the others name.
 */
export function runLogEntryColumnIds(entry: RunLogEntry): string[] | null {
  if (entry.runs.some((run) => run.targetColumnIds === null)) {
    return null;
  }
  return [...new Set(entry.runs.flatMap((run) => run.targetColumnIds ?? []))];
}

/**
 * Whether a column is an analytics column with no workflow it can run.
 *
 * True when the column has no workflow binding, or has one with no
 * `automationWorkflowInstanceId`. The negation of the backend's
 * `has_runnable_workflow` (`midship/heretic/analytics/status.py`), which the
 * server uses to leave such a column out of every session run it dispatches.
 * False for every other column kind.
 */
export function isUnbuiltAnalyticsColumn(column: ProcedureColumn): boolean {
  const analyticsColumn = column.analyticsColumn;
  if (!analyticsColumn) {
    return false;
  }
  const binding = analyticsColumn.workflowBinding;
  return binding === null || binding.automationWorkflowInstanceId === null;
}

/**
 * The targets an optimistic run may carry: `targetColumnIds` with every
 * unbuilt analytics column taken out, since the server never runs one and a
 * placeholder covering it would draw "Running..." in a cell that will not run.
 *
 * The row-wide `null` stays `null` when none of `columns` is unbuilt, so a
 * procedure without one keeps its wildcard placeholder. Otherwise it is spelled
 * out as every other column's id, because the wildcard covers the unbuilt
 * column too. An empty list means nothing targeted can run.
 */
function runnableTargetColumnIds(
  columns: ProcedureColumn[],
  targetColumnIds: string[] | null,
): string[] | null {
  const unbuiltColumnIds = new Set(
    columns.filter(isUnbuiltAnalyticsColumn).map((column) => column.id),
  );
  if (targetColumnIds === null) {
    if (unbuiltColumnIds.size === 0) {
      return null;
    }
    return columns
      .map((column) => column.id)
      .filter((columnId) => !unbuiltColumnIds.has(columnId));
  }
  return targetColumnIds.filter((columnId) => !unbuiltColumnIds.has(columnId));
}

/**
 * An optimistic run for one row, or `null` where the row has no runnable
 * target: the server dispatches no run for such a row, so a placeholder would
 * spin until the next session-runs fetch removed it.
 *
 * `columns` must include every column `targetColumnIds` names (and, for the
 * row-wide `null`, every column of the procedure), since only those are
 * checked for being unbuilt.
 */
export function createPlaceholderSessionRun(
  sessionId: string,
  sourceRowId: string,
  columns: ProcedureColumn[],
  targetColumnIds: string[] | null,
): SessionRun | null {
  const runnableTargets = runnableTargetColumnIds(columns, targetColumnIds);
  if (runnableTargets !== null && runnableTargets.length === 0) {
    return null;
  }
  return createSessionRun(sessionId, sourceRowId, runnableTargets);
}

// Factory function to create a session run
export function createSessionRun(
  sessionId: string,
  sourceRowId: string,
  targetColumnIds: string[] | null = null,
): SessionRun {
  return {
    id: crypto.randomUUID(),
    sessionId,
    sourceRowId,
    state: "queued",
    checkpointId: null,
    failureReason: null,
    targetColumnIds,
    workerId: null,
    eventRunId: null,
    leasedAt: null,
    leaseExpiresAt: null,
    leaseToken: 0,
    cancelled: false,
    attempt: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Utility function to create session runs for unlocked cells in a procedure
 * session.
 *
 * Phase 3 status: BE-WP-3 moved the unlocked-cells computation server-side
 * for `POST /controls/v2/control-session/{id}/generate-work-paper`, so the
 * whole-control-session FE call site (formerly in
 * `AuditWorkPaperViewV2.tsx`'s `handleGenerateWorkPaper`) was deleted in
 * Commit 6 / Commit 10.
 *
 * The per-column rerun endpoints
 * (`/matching/match-session/{psId}/work-paper-data/generate/v2` and
 * `/matching/match-session/{psId}/regenerate-with-instructions/column/{colId}`)
 * are NOT covered by BE-WP-3, so `WorkPaperTableV2.tsx`'s
 * `handleRerunColumn` / `handleRerunColumnWithInstructions` still need
 * client-side unlocked-cells filtering for optimistic session runs.
 *
 * Phase 3 signature change: the previous version took a fully-hydrated
 * `AuditSession`, which is no longer materialized at the per-column
 * rerun call sites (the table-shell is dry; per-row `SourceRow` entries
 * are lazily hydrated via `useSourceRow`). The helper now accepts the
 * lighter inputs it actually needs:
 *   - `procedureSessionId`: the session id stamped on each run
 *   - `sourceRows`: dry envelopes (`id`, plus the verdict-result summaries when
 *     present for attribute-test and analytics lock checks)
 *   - `columns`: the column shape shared by table-shell and AuditSession
 *   - `workpaperData`: required for support-column lock checks
 *   - `getHeavySourceRow`: closure that resolves the hydrated `SourceRow`
 *     for a given id (typically a `useHookCache().getEntry(...)?.data`
 *     read); returns `undefined` when the row has not been hydrated yet
 *   - `targetColumnIds`: subset filter (column-rerun call sites pass one)
 *
 * Fallback behavior: when `getHeavySourceRow(rowId)` is `undefined` the
 * helper cannot evaluate eval-column locks, so it conservatively treats
 * the row as having at least one unlocked column. This matches the
 * server-side filter — the server will skip fully-locked rows on its
 * own; the only cost is a transient optimistic run that the
 * follow-up `fetchSessionRuns(controlSessionId)` will reconcile.
 */
export function createSessionRunsForUnlockedCells(params: {
  procedureSessionId: string;
  sourceRows: Array<{ id: string } & VerdictLockSummaries>;
  columns: ProcedureColumn[];
  workpaperData: WorkpaperDataType;
  getHeavySourceRow: (sourceRowId: string) => SourceRow | undefined;
  targetColumnIds?: string[] | null;
}): SessionRun[] {
  const {
    procedureSessionId,
    sourceRows,
    columns,
    workpaperData,
    getHeavySourceRow,
    targetColumnIds = null,
  } = params;

  const sessionRunsToAdd: SessionRun[] = [];

  // Unbuilt analytics columns never count toward "at least one unlocked": a
  // row whose only unlocked cells are unbuilt gets no run from the server.
  const runnableTargets = runnableTargetColumnIds(columns, targetColumnIds);
  const columnsToCheck = runnableTargets
    ? columns.filter((c) => runnableTargets.includes(c.id))
    : columns;

  for (const drySourceRow of sourceRows) {
    const heavySourceRow = getHeavySourceRow(drySourceRow.id);

    // Check if at least one column is unlocked.
    const hasUnlockedColumn = columnsToCheck.some((column) => {
      if (column.supportColumn) {
        return !isSupportColumnLocked(
          drySourceRow.id,
          column.supportColumn.id,
          workpaperData,
        );
      }
      if (column.evaluatedColumn) {
        // Eval-column locks live on `checkResults`, which is only on the
        // heavy row. Without it, conservatively treat as unlocked — the
        // server is the source of truth and will skip locked cells.
        if (!heavySourceRow) return true;
        return !isEvalColumnLocked(heavySourceRow, column.evaluatedColumn.id);
      }
      if (isVerdictColumn(column)) {
        return !isVerdictColumnLocked(column, drySourceRow);
      }
      return true;
    });

    // If at least one column is unlocked, create a session run
    if (hasUnlockedColumn) {
      sessionRunsToAdd.push(
        createSessionRun(procedureSessionId, drySourceRow.id, runnableTargets),
      );
    }
  }

  return sessionRunsToAdd;
}

// Cardinal edges of a highlighted cell that should draw a border — false
// when the neighbor on that side is also highlighted, so a contiguous
// highlighted region renders as one merged block instead of every cell
// drawing its own full border (see WorkPaperTableV2's per-cell edge
// computation, which walks the RENDERED row/column order and treats two
// wildcard-highlighted rows as adjacent on every column).
export interface HighlightEdges {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
}

// A highlighted cell with every edge true (the default when no
// `HighlightEdges` is supplied — see `getHighlightCellClassName`) draws a
// complete standalone border, matching the pre-region-merge appearance for
// any caller that only tracks the `isHighlighted` boolean.
const ALL_EDGES_HIGHLIGHTED: HighlightEdges = {
  top: true,
  right: true,
  bottom: true,
  left: true,
};

// Tailwind's JIT scanner only picks up arbitrary-value classes that appear
// as complete, literal tokens in source — it can't see a class assembled at
// runtime from smaller concatenated fragments. So every one of the 15
// non-empty top/right/bottom/left combinations is spelled out below in
// full, for both the solid (visible) and fading (dissolving to transparent)
// color states; the 16th, all-edges-false combination needs no border at
// all and isn't listed (a fully-interior cell with highlighted neighbors on
// every side renders with no shadow classes). Each 2px inset shadow stands
// in for one side of the ring this used to be — `box-shadow`, unlike
// `ring`, lets each side be toggled independently, which is what lets
// adjacent highlighted cells merge into a single bordered region instead of
// doubling their shared edge.
const EDGE_SHADOW_CLASSES: Record<string, { solid: string; fading: string }> = {
  // left
  "0001": {
    solid: "shadow-[inset_2px_0_0_0_var(--color-midship-orange)]",
    fading: "shadow-[inset_2px_0_0_0_transparent]",
  },
  // bottom
  "0010": {
    solid: "shadow-[inset_0_-2px_0_0_var(--color-midship-orange)]",
    fading: "shadow-[inset_0_-2px_0_0_transparent]",
  },
  // right
  "0100": {
    solid: "shadow-[inset_-2px_0_0_0_var(--color-midship-orange)]",
    fading: "shadow-[inset_-2px_0_0_0_transparent]",
  },
  // top
  "1000": {
    solid: "shadow-[inset_0_2px_0_0_var(--color-midship-orange)]",
    fading: "shadow-[inset_0_2px_0_0_transparent]",
  },
  // bottom+left
  "0011": {
    solid:
      "shadow-[inset_0_-2px_0_0_var(--color-midship-orange),inset_2px_0_0_0_var(--color-midship-orange)]",
    fading: "shadow-[inset_0_-2px_0_0_transparent,inset_2px_0_0_0_transparent]",
  },
  // right+left
  "0101": {
    solid:
      "shadow-[inset_2px_0_0_0_var(--color-midship-orange),inset_-2px_0_0_0_var(--color-midship-orange)]",
    fading: "shadow-[inset_2px_0_0_0_transparent,inset_-2px_0_0_0_transparent]",
  },
  // right+bottom
  "0110": {
    solid:
      "shadow-[inset_0_-2px_0_0_var(--color-midship-orange),inset_-2px_0_0_0_var(--color-midship-orange)]",
    fading:
      "shadow-[inset_0_-2px_0_0_transparent,inset_-2px_0_0_0_transparent]",
  },
  // top+left
  "1001": {
    solid:
      "shadow-[inset_0_2px_0_0_var(--color-midship-orange),inset_2px_0_0_0_var(--color-midship-orange)]",
    fading: "shadow-[inset_0_2px_0_0_transparent,inset_2px_0_0_0_transparent]",
  },
  // top+bottom
  "1010": {
    solid:
      "shadow-[inset_0_2px_0_0_var(--color-midship-orange),inset_0_-2px_0_0_var(--color-midship-orange)]",
    fading: "shadow-[inset_0_2px_0_0_transparent,inset_0_-2px_0_0_transparent]",
  },
  // top+right
  "1100": {
    solid:
      "shadow-[inset_0_2px_0_0_var(--color-midship-orange),inset_-2px_0_0_0_var(--color-midship-orange)]",
    fading: "shadow-[inset_0_2px_0_0_transparent,inset_-2px_0_0_0_transparent]",
  },
  // right+bottom+left
  "0111": {
    solid:
      "shadow-[inset_0_-2px_0_0_var(--color-midship-orange),inset_2px_0_0_0_var(--color-midship-orange),inset_-2px_0_0_0_var(--color-midship-orange)]",
    fading:
      "shadow-[inset_0_-2px_0_0_transparent,inset_2px_0_0_0_transparent,inset_-2px_0_0_0_transparent]",
  },
  // top+bottom+left
  "1011": {
    solid:
      "shadow-[inset_0_2px_0_0_var(--color-midship-orange),inset_0_-2px_0_0_var(--color-midship-orange),inset_2px_0_0_0_var(--color-midship-orange)]",
    fading:
      "shadow-[inset_0_2px_0_0_transparent,inset_0_-2px_0_0_transparent,inset_2px_0_0_0_transparent]",
  },
  // top+right+left
  "1101": {
    solid:
      "shadow-[inset_0_2px_0_0_var(--color-midship-orange),inset_2px_0_0_0_var(--color-midship-orange),inset_-2px_0_0_0_var(--color-midship-orange)]",
    fading:
      "shadow-[inset_0_2px_0_0_transparent,inset_2px_0_0_0_transparent,inset_-2px_0_0_0_transparent]",
  },
  // top+right+bottom
  "1110": {
    solid:
      "shadow-[inset_0_2px_0_0_var(--color-midship-orange),inset_0_-2px_0_0_var(--color-midship-orange),inset_-2px_0_0_0_var(--color-midship-orange)]",
    fading:
      "shadow-[inset_0_2px_0_0_transparent,inset_0_-2px_0_0_transparent,inset_-2px_0_0_0_transparent]",
  },
  // top+right+bottom+left
  "1111": {
    solid:
      "shadow-[inset_0_2px_0_0_var(--color-midship-orange),inset_0_-2px_0_0_var(--color-midship-orange),inset_2px_0_0_0_var(--color-midship-orange),inset_-2px_0_0_0_var(--color-midship-orange)]",
    fading:
      "shadow-[inset_0_2px_0_0_transparent,inset_0_-2px_0_0_transparent,inset_2px_0_0_0_transparent,inset_-2px_0_0_0_transparent]",
  },
};

const edgeKey = (edges: HighlightEdges): string =>
  `${edges.top ? 1 : 0}${edges.right ? 1 : 0}${edges.bottom ? 1 : 0}${edges.left ? 1 : 0}`;

// Shared by every workpaper grid cell type (DataCell, CheckDecision's two
// render branches) so the highlight/fade treatment stays identical across
// them. The cells' base `transition-all` supplies the transition property —
// this class only adds the fade's timing (`duration-700 ease-out`) while
// highlighted, so it never competes with the base utility over
// `transition-property`, and an unhighlighted cell keeps its default timing.
// `edges` defaults to a full standalone border (`ALL_EDGES_HIGHLIGHTED`) for
// call sites that only know the `isHighlighted` boolean; `WorkPaperTableV2`
// passes the real per-cell edges so a contiguous highlighted region merges
// into one block.
// While fading, every visible edge's shadow (and the fill) animates to
// transparent together — `transition-all` covers `box-shadow` alongside
// `background-color` so the merged border dissolves, not just the fill.
export function getHighlightCellClassName(
  isHighlighted: boolean,
  isHighlightFading: boolean,
  edges: HighlightEdges = ALL_EDGES_HIGHLIGHTED,
): string {
  if (!isHighlighted) return "";
  const bg = isHighlightFading ? "!bg-transparent" : "!bg-orange-50";
  const shadowClasses = EDGE_SHADOW_CLASSES[edgeKey(edges)];
  const shadow = shadowClasses
    ? isHighlightFading
      ? shadowClasses.fading
      : shadowClasses.solid
    : "";
  return `duration-700 ease-out ${bg} ${shadow}`;
}
